import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  APPEARANCE_FONT_PREVIEW_FAMILY,
  AppearanceFontUploadState,
  ICorporateDesign,
  IFont,
  MAX_APPEARANCE_FONT_SIZE_BYTES,
} from '@web/dtos';
import { ButtonComponent } from '../feedback/button/button.component';
import { FontResetDialogComponent } from '../dialogs/font-reset-dialog/font-reset-dialog.component';
import { CorporateDesignStore } from '../../services/api/corporate-design/corporate-design-store.service';
import { FontStore } from '../../services/api/font/font-store.service';
import { CmsTextProviderService } from '../../services/cms-text-provider.service';
import { DialogService } from '../../services/support/dialog/dialog.service';

@Component({
  selector: 'aw-appearance-font-upload',
  imports: [ButtonComponent, NgClass],
  templateUrl: './appearance-font-upload.component.html',
  styleUrl: './appearance-font-upload.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppearanceFontUploadComponent implements OnDestroy {
  private readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly fontStore: FontStore = inject(FontStore);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  protected readonly state: WritableSignal<AppearanceFontUploadState> =
    signal('idle');
  protected readonly selectedFile: WritableSignal<File | null> = signal(null);
  protected readonly activeFont: WritableSignal<IFont | null> = signal(null);
  protected readonly pendingFont: WritableSignal<IFont | null> = signal(null);
  protected readonly activeFileSize: WritableSignal<number | null> =
    signal(null);
  protected readonly isDragging: WritableSignal<boolean> = signal(false);
  protected readonly errorMessage: WritableSignal<string> = signal('');
  public readonly draftChange: OutputEmitterRef<boolean> = output<boolean>();
  protected readonly previewFontFamily: string = APPEARANCE_FONT_PREVIEW_FAMILY;
  protected readonly activeFontName: Signal<string> = computed(
    (): string =>
      this.selectedFile()?.name ??
      this.activeFont()?.name ??
      this.cmsTextProvider.appearanceText.fontDefaultName
  );
  protected readonly displayedFilename: Signal<string> = computed(
    (): string =>
      this.selectedFile()?.name ??
      this.pendingFont()?.name ??
      this.activeFont()?.name ??
      ''
  );
  protected readonly displayedFileSize: Signal<string> = computed(
    (): string => {
      const size: number | null =
        this.selectedFile()?.size ?? this.activeFileSize();
      return size === null ? '' : this.formatFileSize(size);
    }
  );

  private initialized: boolean = false;
  private uploadOperation: number = 0;
  private previewStyleElement: HTMLStyleElement | null = null;
  private previewObjectUrl: string | null = null;
  private defaultFontSelected: boolean = false;

  constructor() {
    effect((): void => {
      const corporateDesign: ICorporateDesign =
        this.corporateDesignStore.corporateDesign();
      if (
        this.initialized ||
        this.corporateDesignStore.isLoading() ||
        this.fontStore.isLoading()
      ) {
        return;
      }

      this.initialized = true;
      const font: IFont | null = corporateDesign.font;
      this.activeFont.set(font);
      this.state.set(font ? 'active' : 'idle');
      if (font) {
        void this.loadStoredFont(font);
      }
    });
  }

  protected selectFile(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];
    if (file) {
      void this.uploadFile(file);
    }
    input.value = '';
  }

  protected handleDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(true);
  }

  protected handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
  }

  protected handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    const file: File | undefined = event.dataTransfer?.files[0];
    if (file) {
      void this.uploadFile(file);
    }
  }

  protected cancelUpload(): void {
    this.uploadOperation++;
    this.pendingFont.set(null);
    this.selectedFile.set(null);
    this.errorMessage.set('');
    this.defaultFontSelected = false;
    this.restoreCurrentState();
  }

  protected applyFont(): void {
    if (!this.selectedFile()) {
      return;
    }
    this.defaultFontSelected = false;
    this.state.set('active');
    this.draftChange.emit(true);
  }

  protected openResetDialog(): void {
    this.dialogService
      .openDialog(FontResetDialogComponent, {
        headline: this.cmsTextProvider.appearanceText.fontResetTitle,
        width: '32rem',
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if (response?.submit) {
          this.restoreDefaultFont();
        }
      });
  }

  protected formatFileSize(size: number): string {
    return `${new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(size / 1000)} KB`;
  }

  private async uploadFile(file: File): Promise<void> {
    const extension: string = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['ttf', 'woff2'].includes(extension)) {
      this.showError(this.cmsTextProvider.appearanceText.fontInvalidTypeError);
      return;
    }
    if (file.size > MAX_APPEARANCE_FONT_SIZE_BYTES) {
      this.showError(this.cmsTextProvider.appearanceText.fontTooLargeError);
      return;
    }

    const normalizedFile: File = new File([file], file.name, {
      type: extension === 'woff2' ? 'font/woff2' : 'font/ttf',
    });
    const operation: number = ++this.uploadOperation;
    this.selectedFile.set(normalizedFile);
    this.pendingFont.set(null);
    this.errorMessage.set('');
    this.state.set('uploading');

    try {
      if (operation !== this.uploadOperation) {
        return;
      }
      await this.loadPreviewFont(normalizedFile, normalizedFile.name);
      this.state.set('preview');
    } catch {
      if (operation === this.uploadOperation) {
        this.showError(this.cmsTextProvider.appearanceText.fontUploadError);
      }
    }
  }

  private restoreDefaultFont(): void {
    this.defaultFontSelected = true;
    this.activeFont.set(null);
    this.activeFileSize.set(null);
    this.selectedFile.set(null);
    this.pendingFont.set(null);
    this.clearPreviewFont();
    this.state.set('idle');
    this.draftChange.emit(true);
  }

  public getPendingFile(): File | null {
    return this.state() === 'active' ? this.selectedFile() : null;
  }

  public isDefaultSelected(): boolean {
    return this.defaultFontSelected;
  }

  public resetDraft(): void {
    this.uploadOperation++;
    this.defaultFontSelected = false;
    this.selectedFile.set(null);
    this.pendingFont.set(null);
    this.errorMessage.set('');
    const storedFont: IFont | null =
      this.corporateDesignStore.corporateDesign().font;
    this.activeFont.set(storedFont);
    this.restoreCurrentState();
  }

  public commitSavedFont(font: IFont | null): void {
    this.defaultFontSelected = false;
    this.activeFont.set(font);
    this.activeFileSize.set(this.selectedFile()?.size ?? this.activeFileSize());
    this.selectedFile.set(null);
    this.pendingFont.set(null);
    this.state.set(font ? 'active' : 'idle');
    if (font) {
      void this.loadStoredFont(font);
    } else {
      this.clearPreviewFont();
    }
  }

  private restoreCurrentState(): void {
    this.state.set(this.activeFont() ? 'active' : 'idle');
    const activeFont: IFont | null = this.activeFont();
    if (activeFont) {
      void this.loadStoredFont(activeFont);
    } else {
      this.clearPreviewFont();
    }
  }

  private showError(message: string): void {
    this.errorMessage.set(message);
    this.pendingFont.set(null);
    this.selectedFile.set(null);
    this.state.set('error');
  }

  private async loadStoredFont(font: IFont): Promise<void> {
    try {
      const blob: Blob = await this.fontStore.getFile(font);
      this.activeFileSize.set(blob.size);
      await this.loadPreviewFont(blob, font.name);
    } catch {
      this.activeFileSize.set(null);
    }
  }

  private async loadPreviewFont(source: Blob, filename: string): Promise<void> {
    this.clearPreviewFont();
    this.previewObjectUrl = URL.createObjectURL(source);
    const fontFormat: string = filename.toLowerCase().endsWith('.woff2')
      ? 'woff2'
      : 'truetype';
    this.previewStyleElement = document.createElement('style');
    this.previewStyleElement.textContent = `
      @font-face {
        font-family: '${APPEARANCE_FONT_PREVIEW_FAMILY}';
        src: url('${this.previewObjectUrl}') format('${fontFormat}');
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }
    `;
    document.head.appendChild(this.previewStyleElement);
    await document.fonts.load(`1rem "${APPEARANCE_FONT_PREVIEW_FAMILY}"`);
  }

  private clearPreviewFont(): void {
    if (this.previewStyleElement) {
      this.previewStyleElement.remove();
      this.previewStyleElement = null;
    }
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.uploadOperation++;
    this.clearPreviewFont();
  }
}
